import { Link, useLocation } from "@/lib/router-compat";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SolutionData } from "@/data/solutionsData";
import { getSolutionBySlug } from "@/data/solutionsData";
import { useLanguage } from "@/hooks/useLanguage";
import { ES_SOLUTION_PATH_MAP, ZH_SOLUTION_PATH_MAP } from "@/i18n/solutionDetail";
import { uiT } from "@/i18n/ui";
import { localizeHref } from "@/lib/localizeHref";

interface Props {
  solution: SolutionData;
  onCtaClick: () => void;
}

const SolutionHero = ({ solution, onCtaClick }: Props) => {
  const lang = useLanguage();
  const t = uiT(lang);
  const categoryLabel = solution.category === "pharma" ? t("pharmaBiotech") : t("medDevicesIVD");
  const parentSolution = solution.parentSlug ? getSolutionBySlug(solution.parentSlug) : undefined;

  const esPathReverse = Object.fromEntries(Object.entries(ES_SOLUTION_PATH_MAP).map(([es, en]) => [en, es]));
  const zhPathReverse = Object.fromEntries(Object.entries(ZH_SOLUTION_PATH_MAP).map(([zh, en]) => [en, zh]));
  const resolveLink = (enPath: string) => {
    if (lang === "es") return esPathReverse[enPath] || enPath;
    if (lang === "zh-cn") return zhPathReverse[enPath] || enPath;
    return enPath;
  };
  const solutionsLink = localizeHref("/en/solutions", lang);
  const homeLink = localizeHref("/", lang);

  return (
    <section className="relative text-primary-foreground pt-44 pb-16 sm:pb-20 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={solution.heroImage}
          alt={`${solution.title} – ${solution.subtitle}`}
          className="w-full h-full object-cover"
          fetchPriority="high"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/75 to-primary/50" />
      </div>
      <div className="relative z-10 container mx-auto px-6 lg:px-10 max-w-4xl">
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex items-center gap-2 text-xs text-primary-foreground/40 tracking-wide uppercase">
            <li><Link to={homeLink} className="hover:text-primary-foreground/70 transition-colors">{t("home")}</Link></li>
            <li aria-hidden="true" className="text-primary-foreground/20">/</li>
            <li><Link to={solutionsLink} className="hover:text-primary-foreground/70 transition-colors">{t("solutions")}</Link></li>
            {parentSolution && (
              <>
                <li aria-hidden="true" className="text-primary-foreground/20">/</li>
                <li><Link to={resolveLink(parentSolution.path)} className="hover:text-primary-foreground/70 transition-colors truncate max-w-[200px]">{parentSolution.title}</Link></li>
              </>
            )}
            <li aria-hidden="true" className="text-primary-foreground/20">/</li>
            <li className="text-primary-foreground/60 truncate max-w-[200px]">{solution.title}</li>
          </ol>
        </nav>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="px-3 py-1 rounded-full bg-primary-foreground/10 text-xs font-medium text-primary-foreground/60">
              {categoryLabel}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            {solution.title}
          </h1>
          <p className="text-lg text-primary-foreground/70 max-w-2xl mb-8">
            {solution.subtitle}
          </p>
          {solution.hubspotFormId && (
            <Button
              size="lg"
              className="rounded-full px-8 bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg"
              onClick={onCtaClick}
            >
              {t("talkToExpert")} <ArrowRight size={16} className="ml-2" />
            </Button>
          )}
          {solution.partnerLogo && solution.partnerLogo.length > 0 && (
            <div className="flex items-center gap-6 mt-8">
              {solution.partnerLogo.map((logo, i) => (
                <img
                  key={i}
                  src={logo.src}
                  alt={logo.alt}
                  className="h-20 sm:h-24 lg:h-28 w-auto brightness-0 invert opacity-80"
                />
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionHero;
